import React, { useEffect, useRef } from 'react';
import { Form } from '@themesberg/react-bootstrap';
import { allUserPermissions, PERMISSION_MODULES } from '../utils/userPermissions';

const actionLabels = { view: 'View', add: 'Add', edit: 'Edit', delete: 'Delete' };

const UserPermissionFields = ({
  formik,
  existingUser = false,
  rolePermissions = null,
  title = 'User Permissions',
  description,
}) => {
  const inherited = formik.values.permissions == null;
  const roleMap = rolePermissions && !Array.isArray(rolePermissions) ? rolePermissions : null;
  const fallback = existingUser && roleMap == null ? allUserPermissions() : (roleMap || {});
  const permissions = inherited ? fallback : (formik.values.permissions && !Array.isArray(formik.values.permissions)
    ? formik.values.permissions : {});
  const selectAllRef = useRef(null);
  const columnRefs = useRef({});
  const selectedActions = PERMISSION_MODULES.flatMap(module =>
    module.actions.map(action => (permissions[module.key] || []).includes(action)));
  const allSelected = selectedActions.every(Boolean);
  const someSelected = selectedActions.some(Boolean);
  const columnState = Object.fromEntries(Object.keys(actionLabels).map(action => {
    const flags = PERMISSION_MODULES
      .filter(module => module.actions.includes(action))
      .map(module => (permissions[module.key] || []).includes(action));
    return [action, { all: flags.every(Boolean), some: flags.some(Boolean) }];
  }));
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected && !allSelected;
    Object.entries(columnState).forEach(([action, state]) => {
      const input = columnRefs.current[action];
      if (input) input.indeterminate = state.some && !state.all;
    });
  }, [allSelected, someSelected, columnState]);

  const assign = next => formik.setFieldValue('permissions', next);
  const changePermission = (module, action, enabled) => {
    const current = permissions[module] || [];
    assign({
      ...permissions,
      [module]: enabled ? [...new Set([...current, action])] : current.filter(item => item !== action),
    });
  };
  const changeColumn = (action, enabled) => {
    assign(Object.fromEntries(PERMISSION_MODULES.map(module => {
      const current = permissions[module.key] || [];
      if (!module.actions.includes(action)) return [module.key, current];
      return [module.key, enabled
        ? [...new Set([...current, action])]
        : current.filter(item => item !== action)];
    })));
  };
  const permissionErrors = Object.entries(formik.errors)
    .filter(([key]) => key === 'permissions' || key.startsWith('permissions.'))
    .flatMap(([, value]) => typeof value === 'string' ? [value] : []);
  const help = description || (title === 'Role Permissions'
    ? 'Users with this role receive these actions. You can still override them on an individual user.'
    : existingUser && inherited
      ? (roleMap != null
        ? 'This user currently uses the role defaults. Changing any action saves an override for this user only.'
        : 'This existing user currently has all actions enabled. Uncheck any actions you want to remove.')
      : inherited
        ? 'These start from the selected role. Change them only if this user needs different access.'
        : 'These actions override the selected role for this user only.');

  return (
    <fieldset className="border rounded p-3 mb-3" disabled={formik.isSubmitting}>
      <legend className="h6 mb-2">{title}</legend>
      <p className="text-muted mb-3">{help}</p>
      {title !== 'Role Permissions' && (
        <div className="mb-3">
          <button type="button" className="btn btn-link p-0" disabled={inherited}
            onClick={() => assign(null)}>
            Use role defaults
          </button>
        </div>
      )}
      <div className="form-check mb-3">
        <input
          ref={selectAllRef}
          id="permission-select-all"
          className="form-check-input"
          type="checkbox"
          checked={allSelected}
          onChange={event => assign(event.target.checked ? allUserPermissions() : {})}
        />
        <label className="form-check-label fw-bold" htmlFor="permission-select-all">Select All</label>
      </div>
      <div className="table-responsive">
        <table className="table align-middle mb-0">
          <caption className="visually-hidden">Allowed actions by module</caption>
          <thead>
            <tr>
              <th scope="col">Module</th>
              {Object.entries(actionLabels).map(([action, label]) => (
                <th key={action} scope="col" className="text-center">
                  <div>{label}</div>
                  <input
                    ref={element => { columnRefs.current[action] = element; }}
                    id={'permission-select-all-' + action}
                    className="form-check-input mt-2"
                    type="checkbox"
                    aria-label={'Select all ' + label}
                    checked={columnState[action].all}
                    onChange={event => changeColumn(action, event.target.checked)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_MODULES.map(module => (
              <tr key={module.key}>
                <th scope="row">{module.label}</th>
                {Object.entries(actionLabels).map(([action, label]) => (
                  <td key={action} className="text-center">
                    {module.actions.includes(action) ? (
                      <Form.Check
                        type="checkbox"
                        id={'permission-' + module.key + '-' + action}
                        aria-label={module.label + ': ' + label}
                        className="d-inline-block mb-0"
                        checked={(permissions[module.key] || []).includes(action)}
                        onChange={event => changePermission(module.key, action, event.target.checked)}
                      />
                    ) : <span aria-label="Not available">-</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {permissionErrors.length > 0 && <div role="alert" className="text-danger mt-2">{permissionErrors.join(' ')}</div>}
    </fieldset>
  );
};

export default UserPermissionFields;
